import { type MaybeRef, type Ref, computed, ref, unref } from "vue";
import { useI18n } from "vue-i18n";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import cloneDeep from "lodash/cloneDeep";
import { storeToRefs } from "pinia";

import { convertToArray } from "@/helpers/common";
import { buildMessageContent } from "@/helpers/messages";
import { useNotification } from "@/hooks/notifications";
import { QUERY_KEYS as COMMUNICATION_QUERY_KEYS } from "@/hooks/queries/useCommunication";
import { aiAgentsApiService } from "@/services/modules/aiAgents";
import communicationService from "@/services/modules/communication";
import emailService from "@/services/modules/emails";
import notesService from "@/services/modules/notes";
import { useFiles } from "@/stores/ai/files";
import useEmailsStore from "@/stores/emails";
import useNotesStore from "@/stores/notes";

import type { EmailFormPayload } from "@/components/emailSending/EmailForm.vue";
import type { LogEndpointType } from "@/enums/communicationLogs";
import type { IPaginatedResponse } from "@/models/common";
import type {
  CallLogData,
  CallNotePayload,
  CommunicationFilters,
  CommunicationLogBase,
  CommunicationLogComment,
  CommunicationLogResponse,
  EmailThreadItem,
  TaskLogData,
} from "@/models/communicationLogs";
import type { EmailModalDataRequestPayload } from "@/models/emails";
import type { Note, NotePayload } from "@/models/notes";

const PER_PAGE_STEP = "15";
export const THREADS_PER_PAGE_STEP = "30";
export const COMMUNICATION_LOGS_KEY = "communication-logs";
const EMAIL_MODAL_DATA_KEY = "email-modal-data";
const EMAIL_THREAD_KEY = "email-thread";
const AI_AGENT_SESSION_KEY = "ai-agent-session";

const QUERY_KEYS = {
  communicationLogs: (
    params: Ref<Partial<CommunicationFilters>>,
    isThreaded?: MaybeRef<boolean>
  ) => [COMMUNICATION_LOGS_KEY, params, isThreaded],
  emailModalData: (application_ids: string[]) => [
    EMAIL_MODAL_DATA_KEY,
    application_ids,
  ],
  emailThread: (id: Ref<number>) => [EMAIL_THREAD_KEY, id],
} as const;

const getNotePredicate = ({
  queryKey,
  invalidateAll,
}: {
  queryKey: readonly unknown[];
  invalidateAll: MaybeRef<boolean>;
}) => {
  const invalidateAllValue = computed(() => unref(invalidateAll));
  if (invalidateAllValue.value) {
    return queryKey[0] === COMMUNICATION_LOGS_KEY;
  }
  const queryKeyTypeParam = convertToArray(
    (queryKey[1] as Record<string, string>)?.type
  );
  return (
    queryKey[0] === COMMUNICATION_LOGS_KEY && queryKeyTypeParam.includes("note")
  );
};

export interface CommentPayload {
  logId: CommunicationLogBase["id"];
  type: LogEndpointType;
  comment: CommunicationLogComment["comment"];
}

export const useCommunicationLogsQuery = (
  params: Ref<Partial<CommunicationFilters>>,
  isThreaded: Ref<boolean> = ref(false)
) => {
  const queryKey = computed(() =>
    QUERY_KEYS.communicationLogs(params, isThreaded)
  );

  const prepareParams = (
    params: MaybeRef<Partial<CommunicationFilters>> = {},
    pageParam: number
  ): Partial<CommunicationFilters> => {
    const preparedParams = cloneDeep(unref(params));

    if (!preparedParams.per_page) {
      preparedParams.per_page = isThreaded.value
        ? THREADS_PER_PAGE_STEP
        : PER_PAGE_STEP;
    }

    preparedParams.page = String(pageParam);

    /**
     * TODO: update this logic when we support multiple types.
     * For now we can only pass one type,
     * but this will be updated in the following PRs
     */
    if (preparedParams.type?.length) {
      preparedParams.type = convertToArray(preparedParams.type)[0];
    }

    const arrayParams: Array<keyof CommunicationFilters> = [
      "application_ids",
      "assignee_ids",
      "call_session_caller_ids",
      "note_created_at",
      "ai_agent_ids",
      "ai_agent_initiator_ids",
      "ai_agent_created_at",
    ];

    arrayParams.forEach((param) => {
      const value = preparedParams[param];

      if (value) {
        const valueArray = Array.isArray(value) ? value : [value];
        Object.assign(preparedParams, { [param]: valueArray });
      }
    });

    if (isThreaded.value) {
      preparedParams.is_thread = "1";
    }

    return preparedParams;
  };

  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey,
    queryFn: async ({ pageParam = 1 }): Promise<CommunicationLogResponse> => {
      const preparedParams = prepareParams(params, pageParam);
      const response =
        await communicationService.getCommunicationList(preparedParams);
      return response;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
  });
};

export const useCreateCommentMutation = (
  sms_conversation_id?: number | null
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CommentPayload): Promise<CommunicationLogComment> =>
      communicationService.createComment(
        payload.type,
        payload.logId,
        payload.comment
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
      if (sms_conversation_id) {
        queryClient.invalidateQueries({
          queryKey:
            COMMUNICATION_QUERY_KEYS.getSmsConversation(sms_conversation_id),
        });
      }
    },
  });
};

export const useUpdateCommentMutation = (
  sms_conversation_id?: number | null
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: CommentPayload & { commentId: CommunicationLogComment["id"] }
    ): Promise<CommunicationLogComment> =>
      communicationService.updateComment(
        payload.type,
        payload.logId,
        payload.commentId,
        payload.comment
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
      if (sms_conversation_id) {
        queryClient.invalidateQueries({
          queryKey:
            COMMUNICATION_QUERY_KEYS.getSmsConversation(sms_conversation_id),
        });
      }
    },
  });
};

export const useDeleteCommentMutation = (
  sms_conversation_id?: number | null
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: Omit<CommentPayload, "comment"> & {
        commentId: CommunicationLogComment["id"];
      }
    ) =>
      communicationService.deleteComment(
        payload.type,
        payload.logId,
        payload.commentId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
      if (sms_conversation_id) {
        queryClient.invalidateQueries({
          queryKey:
            COMMUNICATION_QUERY_KEYS.getSmsConversation(sms_conversation_id),
        });
      }
    },
  });
};

export const useUpdateTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: Partial<
        TaskLogData & { description: string; assignee_id: number }
      >
    ) => {
      return communicationService.updateTask(Number(payload.id), payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
  });
};

export const useDeleteTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: number) => {
      return communicationService.deleteTask(taskId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
  });
};

export const useUpdateCallNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: CallNotePayload & { callSessionId: CallLogData["id"] }
    ): Promise<CallLogData> =>
      communicationService.updateCallNote(payload.callSessionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
  });
};

export const useCreateNoteMutation = (
  invalidateAll: MaybeRef<boolean> = false
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: NotePayload): Promise<Note> =>
      notesService.createNote(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          getNotePredicate({ queryKey, invalidateAll }),
      });
    },
  });
};

export const useUpdateNoteMutation = (
  invalidateAll: MaybeRef<boolean> = false
) => {
  const queryClient = useQueryClient();
  const { getNotableId } = useNotesStore();
  const { showMessage } = useNotification();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (payload: NotePayload): Promise<Note | void> => {
      const currentPayload: NotePayload = {
        ...payload,
        notable_id: getNotableId(payload.notable_id),
      };
      if (!payload.id) {
        showMessage(t("COMMON.ERROR_OCCURRED"), "error");
        return new Promise((resolve) => resolve());
      }
      return notesService.updateNote(currentPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          getNotePredicate({ queryKey, invalidateAll }),
      });
    },
  });
};

export const useDeleteNoteMutation = (
  invalidateAll: MaybeRef<boolean> = false
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: Note["id"]) => notesService.deleteNote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          getNotePredicate({ queryKey, invalidateAll }),
      });
    },
  });
};

export const useEmailModalDataQuery = (
  payload: Ref<EmailModalDataRequestPayload | null>
) => {
  const store = useEmailsStore();
  const { modalData } = storeToRefs(store);

  const queryKey = computed(() => {
    const value = unref(payload);
    if (!value) {
      return [EMAIL_MODAL_DATA_KEY, null];
    }
    const ids =
      "application_ids" in value
        ? value.application_ids
        : value.email_thread_ids;
    return [EMAIL_MODAL_DATA_KEY, ids];
  });

  const enabled = computed(() => {
    const value = unref(payload);
    if (!value) {
      return false;
    }
    const ids =
      "application_ids" in value
        ? value.application_ids
        : value.email_thread_ids;
    return !!ids?.length;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const data = await emailService.getApplicationEmailModalData(
        unref(payload)!
      );
      modalData.value = data;
      return data;
    },
    enabled,
  });
};

export const useSendEmailMutation = (invalidateAll = false) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      email: EmailFormPayload;
      files: File[];
      replyEmailId?: number;
    }): Promise<void> => {
      if (payload.replyEmailId) {
        await emailService.replyToEmail({
          emailId: payload.replyEmailId,
          body: payload.email.body,
          files: payload.files ?? [],
        });
      } else {
        await emailService.sendApplicationEmail(payload.email, payload.files);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (invalidateAll) {
            return (
              queryKey[0] === COMMUNICATION_LOGS_KEY ||
              queryKey[0] === EMAIL_THREAD_KEY
            );
          }
          return (
            (queryKey[0] === COMMUNICATION_LOGS_KEY &&
              (queryKey[1] as Record<string, string>)?.type === "email") ||
            queryKey[0] === EMAIL_THREAD_KEY
          );
        },
      });
    },
  });
};

export const useReplyToEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      emailId: number;
      body: string;
      files: File[];
    }): Promise<void> => {
      await emailService.replyToEmail(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === COMMUNICATION_LOGS_KEY ||
          queryKey[0] === EMAIL_THREAD_KEY,
      });
    },
  });
};

const invalidateEmailQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === COMMUNICATION_LOGS_KEY ||
      queryKey[0] === EMAIL_THREAD_KEY,
  });
};

export const useMarkEmailsAsSpamMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (emailIds: number[]) => emailService.markAsSpam(emailIds),
    onSuccess: () => invalidateEmailQueries(queryClient),
  });
};

export const useUnmarkEmailsAsSpamMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (emailIds: number[]) => emailService.removeSpam(emailIds),
    onSuccess: () => invalidateEmailQueries(queryClient),
  });
};

export const useEmailThreadQuery = (id: Ref<number>) => {
  const queryKey = computed(() => QUERY_KEYS.emailThread(id));

  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey,
    queryFn: async (): Promise<IPaginatedResponse<EmailThreadItem>> => {
      const response = await emailService.getEmailThread(id.value);
      return response;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    enabled: computed(() => !!unref(id)),
  });
};

export const useAiAgentSessionQuery = (sessionId: Ref<string>) => {
  return useQuery({
    queryKey: [AI_AGENT_SESSION_KEY, sessionId],
    queryFn: () => aiAgentsApiService.getAgentSession(sessionId.value),
    enabled: computed(() => !!sessionId.value),
  });
};

export const usePromptAgentMutation = () => {
  const queryClient = useQueryClient();
  const { uploadFiles } = useFiles();

  return useMutation({
    mutationFn: async ({
      sessionId,
      message,
      files = [],
    }: {
      sessionId: string;
      message: string;
      files?: File[];
    }) => {
      const { fileBlocks } = await uploadFiles(files, Number(sessionId));
      const messageContent = buildMessageContent(message, fileBlocks);

      await aiAgentsApiService.promptAgent({
        session_id: sessionId,
        content: messageContent,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === AI_AGENT_SESSION_KEY ||
          queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
  });
};
